// The predefined legends' assignment to a layout's keycaps, out of TextEditor and
// LegendsEditScreen (backfill PR 24): every keycap gets the texts and icons of the legend with
// its keycap ID, each merged over the caller's default entry and shown or hidden by the legend
// set's settings (operating system, configuration style, legend style, alternative legend); a
// keycap without a legend loses its entries.
import { deepClone, deepMerge } from './deepObjects';

export const assignLegendsToLayout = (
  layout,
  legends,
  filters,
  { defaultTextObject, defaultIconObject }
) => {
  const { configurationStyle, os, legendStyle } = filters;
  const legendMap = new Map(); // Create a Map to store legends by their IDs for efficient lookup.

  legends.forEach((legend) => {
    legendMap.set(legend.id, legend);
  }); // Iterate over the array of legends and populate the Map with key-value pairs

  const updatedLayout = layout.map((row) =>
    row.map((keycap) => {
      const matchingLegend = legendMap.get(keycap.keycapId);

      if (matchingLegend) {
        const updatedTexts = {};
        const updatedIcons = {};

        // Merge and determine visibility for each text object:
        for (const [position, textObj] of Object.entries(
          matchingLegend.texts || {}
        )) {
          const { settings = {} } = textObj;

          // Determine visibility based on filters:
          const shouldDisplayText =
            (!settings.configurationStyle ||
              settings.configurationStyle === configurationStyle) && // if configurationStyle is not defined or the configurationStyle value matches the current configurationStyle setting
            (!settings.os || settings.os === os) && // if os is not defined or the os value matches the current os setting
            (!settings.legendStyle || settings.legendStyle === legendStyle); // if legendStyle is not defined or the legendStyle value matches the current legendStyle setting

          const mergedTextObj = deepMerge(
            deepClone(defaultTextObject),
            deepClone(textObj)
          ); // Merge default values with the legend object (ensure that both the target and source objects are deep-cloned before merging. This avoids unintended reference sharing.)

          updatedTexts[position] = {
            ...mergedTextObj,
            visible: shouldDisplayText && settings.alternativeLegend !== true, // Apply visibility condition
          };
        }

        // Merge and determine visibility for each icon object:
        for (const [position, iconObj] of Object.entries(
          matchingLegend.icons || {}
        )) {
          const { settings = {} } = iconObj;

          // Determine visibility based on filters
          const shouldDisplayIcon =
            (!settings.configurationStyle ||
              settings.configurationStyle === configurationStyle) &&
            (!settings.os || settings.os === os) &&
            (!settings.legendStyle || settings.legendStyle === legendStyle);

          const mergedIconObj = deepMerge(
            deepClone(defaultIconObject),
            deepClone(iconObj)
          ); // Merge default values with the legend object (ensure that both the target and source objects are deep-cloned before merging. This avoids unintended reference sharing.)

          updatedIcons[position] = {
            ...mergedIconObj,
            visible: shouldDisplayIcon && settings.alternativeLegend !== true, // Apply visibility condition
          };
        }

        return {
          ...keycap,
          texts: updatedTexts,
          icons: updatedIcons,
        };
      }

      // If no matching legend, clear texts and icons
      return {
        ...keycap,
        texts: {},
        icons: {},
      };
    })
  );

  return updatedLayout;
}; // this function applies all legends to the keycapData and set's the visibility for the text and icon objects depending on the selected settings
